<script setup lang="ts">
import type { EventDetails, ProductDetails } from "mws-ts-v2-sdk";

import { useActiveMaxBidQuery } from "~/queries/catalog/bids";

type Props = {
  product: ProductDetails;
  event: EventDetails;
  productStarted: boolean;
};
const { product, productStarted } = defineProps<Props>();

const { isWonByCurrentUser, currentUserHasBid, isCurrentUserTheHighestBidder } =
  useProductBids(() => product);

const isBidCostsEnabled = computed(() => !product.finished);

const { data: bidCosts } = useBidCosts({
  product: () => product,
  enabled: isBidCostsEnabled,
});

const { data: activeMaxBid, isLoading: activeMaxBidIsLoading } =
  useActiveMaxBidQuery(() => product);

const { bidError } = useBidError();
const { showMessageToUser } = useVerifyAdyenResponse();
const biddingBlockedInfo = useBiddingBlockedInfo(
  () => bidCosts.value?.biddingBlockedReason,
);

const isBiddingReservationRequired = computed(
  () => bidCosts.value?.preAuthorisationResponse?.isRequired,
);

const isActiveCurrencyMissing = useIsActiveCurrencyMissing(
  () => product.prices,
);
// Sync disabled state between product bid now and product quick bid
// @TODO implement pinia or context store for this
const biddingDisabledSyncState = ref(false);

const isBidButtonVisible = ref(false);
const bidAmountSectionRef = ref<HTMLElement | null>(null);
const isFooterVisible = ref(false);
const route = useRoute();
const { redirectResult } = route.query;
const footerRef = ref<HTMLElement | null>(null);

onMounted(async () => {
  const footer = document.getElementById("footer");
  if (footer) footerRef.value = footer;

  // For 3DS payments, the ones that requires url redirect, this will be the final step
  if (redirectResult && typeof redirectResult === "string") {
    showMessageToUser(redirectResult);
  }
});

useIntersectionObserver(footerRef, (entries) => {
  isFooterVisible.value = Boolean(entries?.[0]?.isIntersecting);
});

useIntersectionObserver(
  bidAmountSectionRef,
  (entries) => {
    isBidButtonVisible.value = Boolean(entries?.[0]?.isIntersecting);
  },
  { threshold: 0.6 },
);

const showStickyBidBar = computed(
  () =>
    !isBidButtonVisible.value && !isFooterVisible.value && !product.finished,
);

const biddingOptionsDisabled = computed(
  () => !productStarted || product.finished || isActiveCurrencyMissing.value,
);

const quickBiddingDisabled = computed(
  () =>
    biddingOptionsDisabled.value ||
    !product.availableQuickBidPrices ||
    Boolean(activeMaxBid.value),
);

const maxBidIsLoading = computed(() => {
  if (!product.allowMaxBidding) return false;

  return activeMaxBidIsLoading.value;
});
</script>

<template>
  <div class="pt-4 md:pt-6">
    <BaseSkeletonLoader
      v-if="maxBidIsLoading"
      width="100%"
      :height="260"
    />
    <template v-else>
      <ProductBidCurrent
        :current-bid="product.prices"
        :bid-error="bidError"
        :product="product"
        :current-user="{
          hasBid: currentUserHasBid,
          hasTopBid: isCurrentUserTheHighestBidder,
        }"
        data-test="product-bid-current"
      />

      <ProductBidMaxBidActive
        v-if="product.allowMaxBidding && activeMaxBid"
        :active-max-bid="activeMaxBid"
        class="mb-1"
        data-cy="product-max-bid-active"
        data-test="product-max-bid-active"
      />

      <ProductCountdownContainer
        :product="product"
        :current-user-won="isWonByCurrentUser"
        :is-product-details-v2="true"
      />

      <p
        v-if="isActiveCurrencyMissing && !product.finished"
        class="my-4"
      >
        {{ $t("errors.activeCurrencyMissingMultiple") }}
      </p>
      <div
        v-if="!bidCosts?.biddingBlocked && !product.finished"
        class="pt-6"
      >
        <LazyProductBidReservationRequiredMessage
          v-if="
            bidCosts?.preAuthorisationResponse &&
            bidCosts.preAuthorisationResponse.reservedAmount &&
            isBiddingReservationRequired
          "
          :threshold-price="bidCosts.preAuthorisationResponse.threshold"
          :reservation-amount="bidCosts.preAuthorisationResponse.reservedAmount"
          data-cy="product-bid-reservation-required-message"
        />
        <ProductBidQuick
          v-if="!quickBiddingDisabled"
          :bid-choices="product.availableQuickBidPrices ?? []"
          :product="product"
          :event="event"
          :disabled="biddingDisabledSyncState"
          data-test="product-bid-quick"
          @update-disabled-state="
            (disabledState) => (biddingDisabledSyncState = disabledState)
          "
        />
        <ProductBidAmount
          v-if="!biddingOptionsDisabled"
          id="bidAmount"
          ref="bidAmountSectionRef"
          data-cy="product-bid-amount"
          :product="product"
          :event="event"
          :active-max-bid="activeMaxBid"
          :disabled="biddingDisabledSyncState"
          data-test="product-bid-amount"
          @update-disabled-state="
            (disabledState) => (biddingDisabledSyncState = disabledState)
          "
        />
      </div>
      <BaseInfoMessage
        v-else-if="!product.finished"
        class="my-3 md:my-4"
        variant="info"
        :title="biddingBlockedInfo.title"
      >
        <p>{{ biddingBlockedInfo.text }}</p>
      </BaseInfoMessage>
    </template>
  </div>

  <Transition name="slide">
    <LazyProductSidebarStickyBidBar
      v-if="!maxBidIsLoading && showStickyBidBar"
      class="block md:hidden"
      :product="product"
      :current-user="{
        hasActiveMaxBid: Boolean(activeMaxBid),
        hasBid: currentUserHasBid,
        hasTopBid: isCurrentUserTheHighestBidder,
      }"
    />
  </Transition>
</template>

<style scoped lang="postcss">
.slide-enter-active,
.slide-leave-active {
  transition: all 0.3s ease;
}

.slide-enter-from,
.slide-leave-to {
  @apply translate-y-full;
}
</style>
